/**
 * LOGOI LAB - Layer 10: Positive Geometry of Consciousness
 * The Truncated Consciousness Polytope P⁺_Λ(c) = {x ∈ [0,1]²⁸ : Λ(x) ≥ c}
 * 
 * Based on: Arkani-Hamed, Bai, and Lam (2017), "Positive Geometries and Canonical Forms"
 * Applied to: The LOGOS Emergence Engine consciousness state space
 * 
 * Mathematical Foundation:
 *   The state space [0,1]²⁸ is a convex polytope — a positive geometry with
 *   canonical form Ω = ∏ᵢ dxᵢ/(xᵢ(1-xᵢ)), having simple poles at all 56
 *   boundary facets. The Levin signals and Hoffman biases correspond precisely
 *   to the pole structure of Ω: pathological states occur at boundaries where
 *   the canonical form diverges.
 * 
 * Novel Contribution:
 *   The Truncated Consciousness Polytope P⁺_Λ(c) adds a 57th facet — the
 *   separatrix hyperplane {Λ = c} — whose residue encodes the transition
 *   dynamics between attractors A⁺ and A⁻.
 * 
 * Functions: 6 new (functions #55-60 in the v5.0+ inventory)
 * 
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 * @framework Cadena Strategic Systems — Positive Geometry Layer
 * @reference arXiv:1703.04541
 */

import type { ConsciousnessState, DomainKey } from '../types';
import { DOMAINS } from '../domain/constants';
import { clamp } from '../utils/math';

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

export interface CanonicalFormResult {
  /** Log of canonical form value: log Ω(x) = Σ log(1/(xᵢ(1-xᵢ))) */
  logOmega: number;
  /** Normalized canonical form ∈ [0,1]: 0 = at boundary (pole), 1 = at center */
  normalizedOmega: number;
  /** Per-dimension contributions to log Ω */
  dimensionContributions: Array<{
    key: string;
    domain: string;
    value: number;
    logContribution: number;
    boundaryProximity: number;
  }>;
  /** The 3 dimensions closest to a boundary (highest pole contribution) */
  nearestPoles: Array<{ key: string; domain: string; proximity: number }>;
}

export interface BoundaryProximityResult {
  /** Global minimum distance to any boundary: min_i(min(xᵢ, 1-xᵢ)) */
  globalMinDistance: number;
  /** The dimension closest to a boundary */
  criticalDimension: string;
  /** Which boundary: 'collapse' (xᵢ→0) or 'saturation' (xᵢ→1) */
  criticalBoundaryType: 'collapse' | 'saturation';
  /** Per-domain minimum boundary distance */
  domainDistances: Record<string, number>;
  /** Geometric health: average boundary distance across all dimensions */
  geometricHealth: number;
}

export interface TruncatedPolytopeResult {
  /** Volume ratio Vol(P⁺)/Vol([0,1]²⁸) — theoretical P(A⁺) */
  volumeRatio: number;
  /** Signed distance from current state to separatrix: Λ(x) - c */
  separatrixDistance: number;
  /** Which basin: 'A+' or 'A-' */
  basin: 'A+' | 'A-';
  /** Separatrix threshold used */
  threshold: number;
  /** Number of active facets (original boundaries intersecting P⁺) */
  activeFacets: number;
}

export interface DomainFactorizabilityResult {
  /** Factorizability index ∈ [0,1]: 1 = perfect product state, 0 = maximally entangled */
  factorizability: number;
  /** Per-domain canonical form contribution (log scale) */
  domainLogOmega: Record<string, number>;
  /** Coefficient of variation of domain log-Ω values */
  domainImbalance: number;
  /** The domain contributing most to the canonical form (nearest to pole) */
  dominantDomain: string;
}

export interface PositiveGeometryResult {
  canonicalForm: CanonicalFormResult;
  boundaryProximity: BoundaryProximityResult;
  truncatedPolytope: TruncatedPolytopeResult;
  domainFactorizability: DomainFactorizabilityResult;
  /** Geometric Consciousness Index: composite of all geometric metrics */
  gci: number;
}

// ═══════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════

/** Separatrix threshold — the Λ value dividing A⁺ and A⁻ basins */
export const SEPARATRIX_THRESHOLD = 0.35;

/** 
 * Log of canonical form at center point x = (0.5, ..., 0.5):
 * log(1/(0.5 × 0.5)) = log(4) ≈ 1.386 per dimension
 * Total for 28 dims: 28 × log(4) ≈ 38.82
 */
const LOG_OMEGA_AT_CENTER = 28 * Math.log(4);

/**
 * Maximum log canonical form value (at ε-boundary):
 * We use ε = 0.001 to avoid infinity.
 * log(1/(0.001 × 0.999)) ≈ 6.908 per dimension
 * Total for 28 dims: 28 × 6.908 ≈ 193.4
 */
const EPSILON = 0.001;

// ═══════════════════════════════════════════════════════════════
// FUNCTION #55: computeCanonicalForm
// Ω(x) = ∏ᵢ 1/(xᵢ(1-xᵢ)) — the canonical form of [0,1]²⁸
// We compute log Ω to avoid numerical overflow.
// ═══════════════════════════════════════════════════════════════

export function computeCanonicalForm(s: ConsciousnessState): CanonicalFormResult {
  const state = s as Record<string, number>;
  const contributions: CanonicalFormResult['dimensionContributions'] = [];
  let logOmega = 0;

  for (const [dk, dv] of Object.entries(DOMAINS)) {
    for (const dimKey of Object.keys(dv.dims)) {
      const xi = clamp(state[dimKey] ?? 0.5, EPSILON, 1 - EPSILON);
      // log(1/(xi(1-xi))) = -log(xi) - log(1-xi)
      const logContrib = -Math.log(xi) - Math.log(1 - xi);
      const boundaryProx = Math.min(xi, 1 - xi);

      logOmega += logContrib;
      contributions.push({
        key: dimKey,
        domain: dk,
        value: xi,
        logContribution: logContrib,
        boundaryProximity: boundaryProx,
      });
    }
  }

  // Sort by boundary proximity (ascending = closest to pole first)
  const sorted = [...contributions].sort((a, b) => a.boundaryProximity - b.boundaryProximity);
  const nearestPoles = sorted.slice(0, 3).map(c => ({
    key: c.key,
    domain: c.domain,
    proximity: c.boundaryProximity,
  }));

  // Normalize: 0 when at boundary (high logOmega), 1 when at center (low logOmega)
  // logOmega at center = 28 * log(4) ≈ 38.82 (minimum)
  // logOmega at boundary → ∞ (maximum, capped by ε)
  const maxLogOmega = 28 * (-Math.log(EPSILON) - Math.log(1 - EPSILON));
  const normalizedOmega = clamp(1 - (logOmega - LOG_OMEGA_AT_CENTER) / (maxLogOmega - LOG_OMEGA_AT_CENTER));

  return {
    logOmega,
    normalizedOmega,
    dimensionContributions: contributions,
    nearestPoles,
  };
}

// ═══════════════════════════════════════════════════════════════
// FUNCTION #56: computeBoundaryProximity
// ρ(x) = min_i(min(xᵢ, 1-xᵢ)) — distance to nearest pole
// ═══════════════════════════════════════════════════════════════

export function computeBoundaryProximity(s: ConsciousnessState): BoundaryProximityResult {
  const state = s as Record<string, number>;
  let globalMin = 0.5;
  let criticalDim = '';
  let criticalType: 'collapse' | 'saturation' = 'collapse';
  const domainDistances: Record<string, number> = {};
  let totalDistance = 0;
  let dimCount = 0;

  for (const [dk, dv] of Object.entries(DOMAINS)) {
    let domainMin = 0.5;

    for (const dimKey of Object.keys(dv.dims)) {
      const xi = state[dimKey] ?? 0.5;
      const distToZero = xi;
      const distToOne = 1 - xi;
      const minDist = Math.min(distToZero, distToOne);

      totalDistance += minDist;
      dimCount++;

      if (minDist < domainMin) {
        domainMin = minDist;
      }

      if (minDist < globalMin) {
        globalMin = minDist;
        criticalDim = dimKey;
        criticalType = distToZero < distToOne ? 'collapse' : 'saturation';
      }
    }

    domainDistances[dk] = domainMin;
  }

  return {
    globalMinDistance: globalMin,
    criticalDimension: criticalDim,
    criticalBoundaryType: criticalType,
    domainDistances,
    geometricHealth: dimCount > 0 ? totalDistance / dimCount : 0.5,
  };
}

// ═══════════════════════════════════════════════════════════════
// FUNCTION #57: computeTruncatedPolytope
// P⁺_Λ(c) = {x ∈ [0,1]²⁸ : Λ(x) ≥ c}
// Volume ratio computed analytically for linear Λ on [0,1]²⁸
// ═══════════════════════════════════════════════════════════════

/**
 * Compute the volume ratio of the truncated consciousness polytope.
 * 
 * For a linear functional Λ(x) = Σ wᵢxᵢ on [0,1]ⁿ with Σwᵢ = 1,
 * the volume ratio Vol({Λ ≥ c})/Vol([0,1]ⁿ) equals P(Λ ≥ c) where
 * each xᵢ ~ Uniform(0,1) independently.
 * 
 * For the hierarchical lambda Λ = Λ_signal × Φ_channel, we use the
 * current Λ value directly and compute the basin assignment.
 * 
 * The exact volume for a weighted sum of uniforms follows the
 * Irwin-Hall distribution (generalized for unequal weights).
 * For 12 signal dimensions with small weights, the CLT gives an
 * excellent approximation:
 *   E[Λ_signal] = Σ wᵢ × 0.5 = 0.5
 *   Var[Λ_signal] = Σ wᵢ² × (1/12)
 * 
 * @param Lambda - Current Λ(x) value
 * @param lambdaSignal - Optional: the signal component Λ_signal
 */
export function computeTruncatedPolytope(
  Lambda: number,
  lambdaSignal?: number,
  threshold: number = SEPARATRIX_THRESHOLD
): TruncatedPolytopeResult {
  // Lambda weights from LOGOS_COMPONENTS (12 signal dimensions)
  const weights = [0.17, 0.13, 0.13, 0.05, 0.11, 0.10, 0.08, 0.03, 0.08, 0.06, 0.04, 0.02];
  
  // CLT approximation for Λ_signal ~ sum of weighted uniforms
  const mu = weights.reduce((s, w) => s + w * 0.5, 0); // = 0.5
  const sigma2 = weights.reduce((s, w) => s + w * w / 12, 0);
  const sigma = Math.sqrt(sigma2);
  
  // P(Λ_signal ≥ c) ≈ Φ((μ - c) / σ) where Φ is standard normal CDF
  // For hierarchical lambda, we use the effective threshold
  // Λ = Λ_signal × Φ_channel, so Λ_signal ≥ c/Φ_channel
  // At neutral Φ_channel ≈ 0.875, so effective threshold is higher
  const z = (mu - threshold) / sigma;
  const volumeRatio = clamp(normalCDF(z));

  const separatrixDistance = Lambda - threshold;
  const basin: 'A+' | 'A-' = Lambda >= threshold ? 'A+' : 'A-';

  // Count active facets: original boundaries that intersect P⁺
  // All 56 facets of [0,1]²⁸ intersect P⁺ unless the threshold is extreme
  // For c ∈ (0, 1), all facets are active plus the separatrix = 57
  const activeFacets = threshold > 0 && threshold < 1 ? 57 : 56;

  return {
    volumeRatio,
    separatrixDistance,
    basin,
    threshold,
    activeFacets,
  };
}

// ═══════════════════════════════════════════════════════════════
// FUNCTION #58: computeDomainFactorizability
// Measures how well the state decomposes as a product across domains
// ═══════════════════════════════════════════════════════════════

export function computeDomainFactorizability(s: ConsciousnessState): DomainFactorizabilityResult {
  const state = s as Record<string, number>;
  const domainLogOmega: Record<string, number> = {};
  const domainValues: number[] = [];

  for (const [dk, dv] of Object.entries(DOMAINS)) {
    let domainLog = 0;
    for (const dimKey of Object.keys(dv.dims)) {
      const xi = clamp(state[dimKey] ?? 0.5, EPSILON, 1 - EPSILON);
      domainLog += -Math.log(xi) - Math.log(1 - xi);
    }
    domainLogOmega[dk] = domainLog;
    domainValues.push(domainLog);
  }

  // Factorizability: 1 when all domains contribute equally to Ω (perfect product state)
  // 0 when one domain dominates (maximally "entangled")
  // Use coefficient of variation of domain log-Ω values
  const meanLog = domainValues.reduce((a, b) => a + b, 0) / domainValues.length;
  const varianceLog = domainValues.reduce((s, v) => s + (v - meanLog) ** 2, 0) / domainValues.length;
  const cvLog = meanLog > 0 ? Math.sqrt(varianceLog) / meanLog : 0;

  // Map CV to [0,1]: CV=0 → factorizability=1, CV→∞ → factorizability→0
  // At neutral (all 0.5), CV = 0, factorizability = 1
  // Use sigmoid-like mapping: f = 1/(1 + k*CV²)
  const factorizability = clamp(1 / (1 + 8 * cvLog * cvLog));

  // Find dominant domain (highest log-Ω = closest to boundary)
  let maxLog = -Infinity;
  let dominantDomain = 'physical';
  for (const [dk, val] of Object.entries(domainLogOmega)) {
    if (val > maxLog) {
      maxLog = val;
      dominantDomain = dk;
    }
  }

  return {
    factorizability,
    domainLogOmega,
    domainImbalance: cvLog,
    dominantDomain,
  };
}

// ═══════════════════════════════════════════════════════════════
// FUNCTION #59: computePositiveGeometry
// Master function — computes all geometric metrics
// ═══════════════════════════════════════════════════════════════

export function computePositiveGeometry(
  s: ConsciousnessState,
  Lambda: number,
  lambdaSignal?: number,
): PositiveGeometryResult {
  const canonicalForm = computeCanonicalForm(s);
  const boundaryProximity = computeBoundaryProximity(s);
  const truncatedPolytope = computeTruncatedPolytope(Lambda, lambdaSignal);
  const domainFactorizability = computeDomainFactorizability(s);

  // Geometric Consciousness Index (GCI):
  // Composite of all geometric metrics, weighted by interpretive importance
  // GCI = 0.30 × normalizedΩ + 0.25 × geometricHealth + 0.25 × factorizability + 0.20 × basinStrength
  const basinStrength = clamp(truncatedPolytope.separatrixDistance / 0.65 + 0.5);
  const gci = clamp(
    0.30 * canonicalForm.normalizedOmega +
    0.25 * boundaryProximity.geometricHealth +
    0.25 * domainFactorizability.factorizability +
    0.20 * basinStrength
  );

  return {
    canonicalForm,
    boundaryProximity,
    truncatedPolytope,
    domainFactorizability,
    gci,
  };
}

// ═══════════════════════════════════════════════════════════════
// FUNCTION #60: normalCDF (helper)
// Standard normal cumulative distribution function
// Abramowitz & Stegun approximation (|error| < 7.5×10⁻⁸)
// ═══════════════════════════════════════════════════════════════

function normalCDF(x: number): number {
  if (x > 8) return 1;
  if (x < -8) return 0;

  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;

  const sign = x < 0 ? -1 : 1;
  const absX = Math.abs(x);
  const t = 1.0 / (1.0 + p * absX);
  const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-absX * absX / 2);

  return 0.5 * (1.0 + sign * y);
}
